// src/components/Home.js
//
// Panel principal — reworked as a KPI dashboard.
//
// Layout (top → bottom):
//   1. Header + year selector
//   2. Four KPI tiles powered by /api/bi/summary/
//        · Ingreso Total (YTD)
//        · Utilidad Neta
//        · Margen
//        · Proyectos Activos (with total as sub)
//   3. Monthly revenue chart (Chart.js bar)
//   4. Quick actions (Nuevo proyecto, Nuevo cliente, Detalle proyectos, Box Score)
//   5. Compact module grid — icon + label, whole card clickable, no button
//
// Everything reads from BI_SUMMARY so there's no new backend work.
//
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { API_URL_BI_SUMMARY, API_URL_BI_TIMELINE } from "../constants";
import { getAuthHeaders } from "../utils/auth";

const BRAND_RED = "#dc3545";
const BRAND_RED_DARK = "#a01e2b";
const INK = "#111827";
const MUTED = "#6b7280";
const RULE = "#e5e7eb";
const PANEL = "#f9fafb";

// --- SVG icons (24-viewbox, currentColor) --------------------------------
const Icon = ({ children, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    {children}
  </svg>
);
const IconRevenue = () => <Icon><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Icon>;
const IconProfit = () => <Icon><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></Icon>;
const IconMargin = () => <Icon><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></Icon>;
const IconProjects = () => <Icon><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></Icon>;
const IconArrowUp = () => <Icon size={14}><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></Icon>;
const IconArrowDown = () => <Icon size={14}><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></Icon>;
const IconPlus = () => <Icon size={16}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Icon>;
// module icons (small)
const IconClientes = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></Icon>;
const IconEquipo = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></Icon>;
const IconProductos = () => <Icon><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></Icon>;
const IconProveedores = () => <Icon><path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></Icon>;
const IconCostos = () => <Icon><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></Icon>;
const IconMovimientos = () => <Icon><line x1="17" y1="7" x2="7" y2="17" /><polyline points="17 17 7 17 7 7" /><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></Icon>;
const IconPagos = () => <Icon><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></Icon>;
const IconBoxScore = () => <Icon><path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" /></Icon>;
const IconAnalytics = () => <Icon><path d="M3 3v18h18" /><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" /></Icon>;
const IconValueStream = () => <Icon><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></Icon>;
const IconCausa = () => <Icon><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></Icon>;

const fmtCurrencyUSD = (v) => {
  const n = Number(v) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
// Compact form for KPI tiles — abbreviates >=1M to $X.XM and >=10k to $XXk,
// so long values don't force the layout to overflow narrow cards.
const fmtCurrencyCompact = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
// Tooltip form — always rounded to the thousand with a k marker, so
// hovering a bar shows "$638k" instead of "$637,667".
const fmtCurrencyK = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs < 1000) return `${sign}$${Math.round(abs)}`;
  return `${sign}$${Math.round(abs / 1000)}k`;
};
const fmtNumber = (v) => (Number(v) || 0).toLocaleString();
const fmtPercent = (v) => `${(Number(v) || 0).toFixed(1)}%`;

// KPI Tile
const KpiTile = ({ label, value, sub, icon, accent = BRAND_RED, delta }) => (
  <div style={styles.kpi}>
    <div style={{ ...styles.kpiIconWrap, color: accent, background: `${accent}14` }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={styles.kpiLabel}>{label}</div>
      <div style={styles.kpiValue}>{value}</div>
      {sub && <div style={styles.kpiSub}>{sub}</div>}
      {delta && (
        <div style={{ ...styles.kpiDelta, color: delta.positive ? "#166534" : delta.negative ? "#b91c1c" : MUTED }}>
          {delta.positive ? <IconArrowUp /> : delta.negative ? <IconArrowDown /> : null}
          <span style={{ marginLeft: 4 }}>{delta.label}</span>
        </div>
      )}
    </div>
  </div>
);

// Compact module card
const ModuleCard = ({ to, icon, label, meta }) => (
  <Link to={to} className="lssi-module-card" style={styles.moduleCard}>
    <span style={styles.moduleIcon}>{icon}</span>
    <span style={styles.moduleBody}>
      <span style={styles.moduleLabel}>{label}</span>
      {meta && <span style={styles.moduleMeta}>{meta}</span>}
    </span>
  </Link>
);

const CURRENT_YEAR = 2026;

// A palette that stays legible next to the LSSI red accent — cool +
// warm tones that stack cleanly on a bar chart without clashing.
const STREAM_PALETTE = [
  "#dc3545", "#0e7c66", "#0369a1", "#7c3aed", "#d97706",
  "#0891b2", "#be185d", "#4d7c0f", "#475569", "#c2410c",
];

const Home = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(CURRENT_YEAR);

  // Separate slice of state for the weekly-by-stream chart so we can refetch
  // it independently when the year changes without waiting for the summary.
  const [streamTimeline, setStreamTimeline] = useState(null);
  const [streamTimelineLoading, setStreamTimelineLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(API_URL_BI_SUMMARY, { headers: getAuthHeaders(), params: { year } })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("BI summary failed:", e);
          setError("No se pudo cargar el resumen. Reintenta en un momento.");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [year]);

  // Weekly revenue by value stream — separate fetch so the year selector
  // updates each independently. Timeline uses ISO dates via start/end.
  useEffect(() => {
    let cancelled = false;
    setStreamTimelineLoading(true);
    const start = `${year}-01-01T00:00:00Z`;
    const end = `${year}-12-31T23:59:59Z`;
    axios
      .get(API_URL_BI_TIMELINE, {
        headers: getAuthHeaders(),
        params: { start_date: start, end_date: end, granularity: 'week', group_by: 'value_stream' },
      })
      .then((r) => {
        if (!cancelled) setStreamTimeline(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("BI timeline failed:", e);
          setStreamTimeline(null);
        }
      })
      .finally(() => !cancelled && setStreamTimelineLoading(false));
    return () => {
      cancelled = true;
    };
  }, [year]);

  const summary = data?.summary || {};
  const counts = data?.counts || {};
  const monthly = data?.trends?.monthly_revenue || [];

  const chartData = useMemo(() => {
    // BISummaryView returns monthly_revenue as [{month: ISO, revenue, project_revenue, online_revenue}].
    const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    const labels = monthly.map((m) => {
      if (m.label) return m.label;
      const raw = m.month || m.period;
      if (raw) {
        const d = new Date(raw);
        if (!isNaN(d)) return monthNames[d.getUTCMonth()];
        const n = Number(raw);
        if (Number.isFinite(n)) return monthNames[(n - 1) % 12] || String(n);
      }
      return "";
    });
    // Projects at the bottom of the stack (brand red), online sales on top (teal).
    // Falls back to `revenue` for pre-split payloads so old data doesn't render blank.
    const projectValues = monthly.map((m) => {
      const v = m.project_revenue;
      return Number(v == null ? (m.revenue || 0) : v);
    });
    const onlineValues = monthly.map((m) => Number(m.online_revenue || 0));
    return {
      labels,
      datasets: [
        {
          label: 'Proyectos',
          data: projectValues,
          backgroundColor: `${BRAND_RED}cc`,
          borderRadius: 4,
          categoryPercentage: 0.92,
          barPercentage: 0.92,
          stack: 'ingreso',
        },
        {
          label: 'Online',
          data: onlineValues,
          backgroundColor: '#0e7c66cc',
          borderRadius: 4,
          categoryPercentage: 0.92,
          barPercentage: 0.92,
          stack: 'ingreso',
        },
      ],
    };
  }, [monthly, year]);

  // Stacked bar: weekly revenue split by value stream
  const streamChartData = useMemo(() => {
    const tl = streamTimeline?.timeline || [];
    const streams = streamTimeline?.streams || [];
    if (tl.length === 0 || streams.length === 0) {
      return { labels: [], datasets: [] };
    }
    const labels = tl.map((p) => {
      // ISO week start — format as "d MMM"
      const d = new Date(p.period);
      const day = d.getUTCDate();
      const month = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][d.getUTCMonth()];
      return `${day} ${month}`;
    });
    const datasets = streams.map((stream, i) => ({
      label: stream,
      data: tl.map((p) => Number(p.streams?.[stream] || 0)),
      backgroundColor: STREAM_PALETTE[i % STREAM_PALETTE.length],
      borderRadius: 4,
      // Stacked bars — no hard cap, fill the week slot generously.
      categoryPercentage: 0.9,
      barPercentage: 0.95,
      stack: 'revenue',
    }));
    return { labels, datasets };
  }, [streamTimeline]);

  const streamChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Leave headroom above the tallest stack for the always-visible total label.
      layout: { padding: { top: 28, right: 8, bottom: 4, left: 4 } },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: MUTED, font: { size: 11 }, boxWidth: 10, boxHeight: 10, padding: 12 },
        },
        tooltip: {
          backgroundColor: "#111827",
          padding: 10,
          titleFont: { size: 12, weight: "600" },
          bodyFont: { size: 12 },
          // Hover still shows the per-stream breakdown; the label above the
          // bar is just the weekly total, so the two complement each other.
          position: "nearest",
          yAlign: "bottom",
          caretPadding: 6,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmtCurrencyK(ctx.parsed.y)}`,
          },
        },
        // Show ONE always-visible label per week (on top of the stack) with
        // the summed weekly revenue across all value streams.
        datalabels: {
          display: (ctx) => ctx.datasetIndex === ctx.chart.data.datasets.length - 1,
          anchor: "end",
          align: "top",
          offset: 4,
          clip: false,
          color: INK,
          font: { size: 11, weight: "600" },
          formatter: (_v, ctx) => {
            const total = ctx.chart.data.datasets.reduce(
              (sum, ds) => sum + (Number(ds.data[ctx.dataIndex]) || 0),
              0
            );
            return total > 0 ? fmtCurrencyK(total) : '';
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: MUTED, font: { size: 10 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 12 },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: RULE, drawBorder: false },
          border: { display: false },
          ticks: {
            color: MUTED,
            font: { size: 11 },
            callback: (v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`),
          },
        },
      },
    }),
    []
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Padding just enough to keep the always-visible total label from being
      // clipped above the tallest bar; the legend now sits at the bottom, so
      // the plot area starts right under the card header.
      layout: { padding: { top: 18, right: 8, bottom: 4, left: 4 } },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { color: MUTED, font: { size: 11 }, boxWidth: 10, boxHeight: 10, padding: 12 },
        },
        tooltip: { enabled: false }, // stacked-total label above the bar covers this
        // Only draw the label on the LAST (top) dataset so we don't get
        // two labels stacked on top of each other. Sum both segments and
        // render the total in $Xk above the bar.
        datalabels: {
          display: (ctx) => ctx.datasetIndex === ctx.chart.data.datasets.length - 1,
          anchor: 'end',
          align: 'top',
          offset: 4,
          clip: false,
          color: INK,
          font: { size: 11, weight: '600' },
          formatter: (_v, ctx) => {
            const total = ctx.chart.data.datasets.reduce(
              (sum, ds) => sum + (Number(ds.data[ctx.dataIndex]) || 0),
              0
            );
            return total > 0 ? fmtCurrencyK(total) : '';
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: MUTED, font: { size: 11 }, autoSkip: false, maxRotation: 0 },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: RULE, drawBorder: false },
          border: { display: false },
          ticks: {
            color: MUTED,
            font: { size: 11 },
            callback: (v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`),
          },
        },
      },
    }),
    []
  );

  const modules = [
    { to: "/clientes", label: "Clientes", icon: <IconClientes />, meta: counts.active_clients ? `${fmtNumber(counts.active_clients)} activos` : null },
    { to: "/partners", label: "Equipo", icon: <IconEquipo />, meta: counts.active_partners ? `${fmtNumber(counts.active_partners)} partners` : null },
    { to: "/productos", label: "Productos", icon: <IconProductos /> },
    { to: "/proveedores", label: "Proveedores", icon: <IconProveedores /> },
    { to: "/costos", label: "Costos", icon: <IconCostos /> },
    { to: "/movimientos", label: "Movimientos", icon: <IconMovimientos /> },
    { to: "/estado_instructor", label: "Pagos", icon: <IconPagos /> },
    { to: "/BoxScore", label: "Box Score", icon: <IconBoxScore /> },
    { to: "/analytics", label: "Analytics", icon: <IconAnalytics /> },
    { to: "/sedes", label: "Value Stream", icon: <IconValueStream /> },
    { to: "/proyecto_con_causa", label: "Proyecto con Causa", icon: <IconCausa /> },
  ];

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Panel principal</h1>
          <p style={styles.headerSub}>
            Vista ejecutiva del año {year} — ingresos, utilidad, márgenes y actividad del portafolio.
          </p>
        </div>
        <div style={styles.yearWrap}>
          <label htmlFor="year" style={styles.yearLabel}>Año</label>
          <select
            id="year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={styles.yearSelect}
          >
            {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </header>

      {error && (
        <div style={styles.error} role="alert">{error}</div>
      )}

      {/* KPI row */}
      <section style={styles.kpiRow}>
        <KpiTile
          label="Ingreso Total"
          value={loading ? "—" : fmtCurrencyCompact(summary.total_revenue)}
          sub={
            loading
              ? `Año ${year} · USD`
              : `Proy: ${fmtCurrencyCompact(summary.project_revenue)} · Online: ${fmtCurrencyCompact(summary.online_revenue)}`
          }
          icon={<IconRevenue />}
          accent="#0e7c66"
        />
        <KpiTile
          label="Utilidad Neta"
          value={loading ? "—" : fmtCurrencyCompact(summary.net_profit)}
          sub={loading ? "—" : `Costo: ${fmtCurrencyCompact(summary.total_expenses)}`}
          icon={<IconProfit />}
          accent={BRAND_RED}
        />
        <KpiTile
          label="Margen"
          value={loading ? "—" : fmtPercent(summary.profit_margin)}
          sub={
            (summary.profit_margin || 0) >= 50
              ? "Meta ≥ 50% — bien"
              : "Meta ≥ 50%"
          }
          icon={<IconMargin />}
          accent="#7c3aed"
        />
        <KpiTile
          label="Proyectos Activos"
          value={loading ? "—" : fmtNumber(counts.active_projects)}
          sub={
            counts.total_projects
              ? `de ${fmtNumber(counts.total_projects)} totales`
              : null
          }
          icon={<IconProjects />}
          accent="#0369a1"
        />
      </section>

      {/* Quick actions — full-width horizontal row above the charts */}
      <section className="lssi-quick-actions" style={styles.quickActionsRow}>
        <Link
          to="/proyectos"
          className="lssi-action-btn lssi-action-btn-primary"
          style={{ ...styles.actionBtn, ...styles.actionBtnPrimary }}
        >
          <IconPlus /> Nuevo proyecto
        </Link>
        <Link to="/clientes" className="lssi-action-btn" style={styles.actionBtn}>
          <IconPlus /> Nuevo cliente
        </Link>
        <Link to="/detalle_proyectos" className="lssi-action-btn" style={styles.actionBtn}>
          <IconProjects /> Ver detalle de proyectos
        </Link>
        <Link to="/BoxScore" className="lssi-action-btn" style={styles.actionBtn}>
          <IconBoxScore /> Abrir Box Score
        </Link>
      </section>

      {/* Monthly chart — now full-width */}
      <section style={styles.chartCard}>
        <div style={styles.cardHeader}>
          <div style={styles.cardHeaderTitle}>Ingreso mensual</div>
          <div style={styles.cardHeaderSub}>{year} · en USD · Proyectos + Online</div>
        </div>
        <div style={styles.chartCanvas}>
          {!loading && chartData.labels.length > 0 ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div style={styles.chartEmpty}>{loading ? "Cargando…" : "Sin datos para este año."}</div>
          )}
        </div>
      </section>

      {/* Weekly revenue by value stream */}
      <section style={styles.streamChartCard}>
        <div style={styles.cardHeader}>
          <div style={styles.cardHeaderTitle}>Ingreso semanal por Value Stream</div>
          <div style={styles.cardHeaderSub}>{year} · barras apiladas en USD</div>
        </div>
        <div style={styles.streamChartCanvas}>
          {streamTimelineLoading ? (
            <div style={styles.chartEmpty}>Cargando…</div>
          ) : streamChartData.datasets.length > 0 ? (
            <Bar data={streamChartData} options={streamChartOptions} />
          ) : (
            <div style={styles.chartEmpty}>Sin datos para este año.</div>
          )}
        </div>
      </section>

      {/* Module grid */}
      <section>
        <div style={styles.sectionLabel}>Módulos</div>
        <div style={styles.moduleGrid}>
          {modules.map((m) => (
            <ModuleCard key={m.to} {...m} />
          ))}
        </div>
      </section>

    </div>
  );
};

// -------------------- Styles --------------------
const styles = {
  page: {
    padding: "8px 4px 40px",
    color: INK,
    fontFamily:
      '-apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    // Contain everything so a stubborn child (like a chart canvas that
    // wants to grow to fit its data) can't push the layout past the viewport.
    minWidth: 0,
    maxWidth: "100%",
    overflowX: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  h1: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: INK,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  headerSub: {
    color: MUTED,
    fontSize: "0.9rem",
    margin: "4px 0 0",
  },
  yearWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  yearLabel: {
    fontSize: "0.75rem",
    color: MUTED,
    fontWeight: 500,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  yearSelect: {
    padding: "6px 10px",
    fontSize: "0.875rem",
    border: `1px solid ${RULE}`,
    borderRadius: 8,
    background: "#ffffff",
    color: INK,
    fontWeight: 500,
    cursor: "pointer",
  },

  kpiRow: {
    display: "grid",
    // Smaller minimum so 4 tiles fit inside the sidebar-shifted viewport
    // without ever forcing horizontal scroll. On truly narrow widths they
    // wrap into 2 rows of 2.
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginBottom: 24,
    minWidth: 0,
  },
  kpi: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 18px",
    background: "#ffffff",
    border: `1px solid ${RULE}`,
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
    minWidth: 0,
    overflow: "hidden",
  },
  kpiIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
  },
  kpiLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: MUTED,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: INK,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  kpiSub: {
    fontSize: "0.75rem",
    color: MUTED,
    marginTop: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  kpiDelta: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.75rem",
    marginTop: 4,
    fontWeight: 500,
  },

  // Horizontal strip of shortcut buttons that sits above the charts.
  quickActionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 20,
  },
  chartCard: {
    background: "#ffffff",
    border: `1px solid ${RULE}`,
    borderRadius: 12,
    padding: "18px 20px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
    minWidth: 0,
    overflow: "hidden",
    marginBottom: 24,
  },
  actionsCard: {
    background: "#ffffff",
    border: `1px solid ${RULE}`,
    borderRadius: 12,
    padding: "18px 20px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  cardHeader: {
    marginBottom: 12,
  },
  cardHeaderTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: INK,
    letterSpacing: "-0.005em",
  },
  cardHeaderSub: {
    fontSize: "0.75rem",
    color: MUTED,
    marginTop: 2,
  },
  chartCanvas: {
    position: "relative",
    height: 240,
  },
  streamChartCard: {
    background: "#ffffff",
    border: `1px solid ${RULE}`,
    borderRadius: 12,
    padding: "18px 20px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
    marginBottom: 24,
    minWidth: 0,
    overflow: "hidden",
  },
  streamChartCanvas: {
    position: "relative",
    height: 320,
    minWidth: 0,
  },
  chartEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: MUTED,
    fontSize: "0.875rem",
  },
  actionList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: 1,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 8,
    textDecoration: "none",
    color: INK,
    fontSize: "0.875rem",
    fontWeight: 500,
    background: PANEL,
    border: `1px solid ${RULE}`,
    transition: "background 0.15s ease, transform 0.15s ease",
  },
  actionBtnPrimary: {
    background: BRAND_RED,
    color: "#ffffff",
    borderColor: BRAND_RED,
    fontWeight: 600,
  },

  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: MUTED,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  moduleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 10,
    marginBottom: 24,
  },
  moduleCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    background: "#ffffff",
    border: `1px solid ${RULE}`,
    borderRadius: 10,
    textDecoration: "none",
    color: INK,
    transition: "all 0.15s ease",
  },
  moduleIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    background: `${BRAND_RED}0f`,
    color: BRAND_RED_DARK,
    flexShrink: 0,
  },
  moduleBody: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  moduleLabel: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: INK,
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  moduleMeta: {
    fontSize: "0.75rem",
    color: MUTED,
    marginTop: 2,
  },

  hr: {
    margin: "32px 0 24px",
    border: 0,
    borderTop: `1px solid ${RULE}`,
  },
  error: {
    padding: "10px 14px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    color: "#b91c1c",
    fontSize: "0.875rem",
    marginBottom: 16,
  },
};

// Hover polish + responsive rules (inline style objects can't express
// :hover or media queries). Injected once on module load; the id guard
// makes subsequent hot reloads a no-op.
if (typeof document !== "undefined" && !document.getElementById("lssi-home-hover")) {
  const s = document.createElement("style");
  s.id = "lssi-home-hover";
  s.textContent = `
    .lssi-module-card:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
      border-color: ${BRAND_RED_DARK} !important;
    }
    .lssi-action-btn:hover { background: #ffffff; }
    .lssi-action-btn-primary:hover { background: ${BRAND_RED_DARK}; }
    /* Below ~900px the chart + acciones side-by-side becomes cramped.
       Stack them so each gets full width. */
    @media (max-width: 900px) {
      .lssi-mid-row {
        grid-template-columns: 1fr !important;
      }
    }
  `;
  document.head.appendChild(s);
}

export default Home;
